import * as React from "react"

/** How fast a wheel notch zooms: one ordinary notch (100px) is about ×1.22. */
const WHEEL_SENSITIVITY = 0.002
/** A line is about this many pixels, for a mouse that scrolls by lines. */
const LINE_HEIGHT = 16
/** The wheel counts as still turning for this long after its last notch. */
const WHEEL_IDLE_MS = 160

/**
 * Ctrl (or ⌘) + mouse wheel zooms, as in every PDF viewer. A trackpad pinch
 * arrives as the same event, so it zooms too. Without the modifier the wheel
 * still scrolls the page.
 *
 * Bound by hand rather than with React's `onWheel`, which React attaches as a
 * passive listener: a passive listener cannot stop the browser zooming the
 * whole page instead.
 *
 * `onZoom` receives the factor to multiply the zoom by and the pointer's
 * position within the element. The returned flag is true while the wheel is
 * turning, so the caller can drop its zoom animation and follow the wheel
 * directly rather than lagging behind it.
 */
export function useCtrlWheelZoom(
  ref: React.RefObject<HTMLElement | null>,
  onZoom: (factor: number, x: number, y: number) => void,
): boolean {
  const onZoomRef = React.useRef(onZoom)
  React.useLayoutEffect(() => {
    onZoomRef.current = onZoom
  })
  const [wheeling, setWheeling] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    let idle: ReturnType<typeof setTimeout> | undefined
    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      const pixels =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * LINE_HEIGHT : event.deltaY
      const rect = el.getBoundingClientRect()
      onZoomRef.current(
        Math.exp(-pixels * WHEEL_SENSITIVITY),
        event.clientX - rect.left,
        event.clientY - rect.top,
      )
      setWheeling(true)
      clearTimeout(idle)
      idle = setTimeout(() => {
        setWheeling(false)
      }, WHEEL_IDLE_MS)
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      el.removeEventListener("wheel", onWheel)
      clearTimeout(idle)
    }
  }, [ref])

  return wheeling
}
